import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PatientListTable = ({ 
  patients = [], 
  onPatientSelect = () => {},
  onGenerateAISummary = () => {},
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterBy, setFilterBy] = useState('all');

  const sortOptions = [
    { value: 'name', label: 'Patient Name' },
    { value: 'lastActivity', label: 'Last Activity' },
    { value: 'avgSeverity', label: 'Symptom Severity' },
    { value: 'riskLevel', label: 'Risk Level' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Patients' },
    { value: 'high', label: 'High Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'low', label: 'Low Risk' },
    { value: 'active', label: 'Active Today' }
  ];

  const filteredAndSortedPatients = useMemo(() => {
    let filtered = patients?.filter(patient => {
      const matchesSearch = patient?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesFilter = filterBy === 'all' || 
        (filterBy === 'active' && patient?.isActiveToday) ||
        (filterBy !== 'active' && patient?.riskLevel === filterBy);
      
      return matchesSearch && matchesFilter;
    });

    return filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'lastActivity') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [patients, searchTerm, sortBy, sortOrder, filterBy]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'high': return 'text-error bg-error/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'low': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getAlertIcon = (patient) => {
    if (patient?.hasAlerts) {
      return patient?.riskLevel === 'high' ? 'AlertTriangle' : 'AlertCircle';
    }
    return null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = today - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className={`bg-card border border-border rounded-lg shadow-soft ${className}`}>
      {/* Header with Search and Filters */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search patients by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              className="w-full sm:w-40"
            />
            
            <Select
              options={filterOptions}
              value={filterBy}
              onChange={setFilterBy}
              placeholder="Filter by"
              className="w-full sm:w-40"
            />
            
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              iconName={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'}
              iconPosition="left"
            >
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </Button>
          </div>
        </div>
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Patient Name</span>
                  {sortBy === 'name' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  )}
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('lastActivity')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Last Activity</span>
                  {sortBy === 'lastActivity' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  )}
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('avgSeverity')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Avg Severity</span>
                  {sortBy === 'avgSeverity' && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                    />
                  )}
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Risk Level</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-right p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedPatients?.map((patient, index) => (
              <tr 
                key={patient?.id}
                className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => onPatientSelect(patient)}
              >
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={18} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{patient?.name}</div>
                      <div className="text-sm text-muted-foreground">ID: {patient?.id}</div>
                    </div>
                    {getAlertIcon(patient) && (
                      <Icon 
                        name={getAlertIcon(patient)} 
                        size={16} 
                        color={patient?.riskLevel === 'high' ? 'var(--color-error)' : 'var(--color-warning)'}
                      />
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-foreground">{formatDate(patient?.lastActivity)}</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(patient.lastActivity)?.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="text-foreground font-medium">{patient?.avgSeverity}/10</div>
                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          patient?.avgSeverity >= 7 ? 'bg-error' :
                          patient?.avgSeverity >= 4 ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${(patient?.avgSeverity / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(patient?.riskLevel)}`}>
                    {patient?.riskLevel?.charAt(0)?.toUpperCase() + patient?.riskLevel?.slice(1)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      patient?.isActiveToday ? 'bg-success' : 'bg-muted-foreground'
                    }`} />
                    <span className="text-sm text-foreground">
                      {patient?.isActiveToday ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onPatientSelect(patient);
                      }}
                      iconName="Eye"
                      iconPosition="left"
                    >
                      View
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onGenerateAISummary(patient);
                      }}
                      iconName="Brain"
                      iconPosition="left"
                    >
                      AI Summary
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden">
        <div className="p-4 space-y-4">
          {filteredAndSortedPatients?.map((patient) => (
            <div
              key={patient?.id}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-soft transition-shadow cursor-pointer"
              onClick={() => onPatientSelect(patient)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{patient?.name}</div>
                    <div className="text-sm text-muted-foreground">ID: {patient?.id}</div>
                  </div>
                </div>
                {getAlertIcon(patient) && (
                  <Icon 
                    name={getAlertIcon(patient)} 
                    size={18} 
                    color={patient?.riskLevel === 'high' ? 'var(--color-error)' : 'var(--color-warning)'}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Last Activity</div>
                  <div className="text-sm font-medium text-foreground">{formatDate(patient?.lastActivity)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Avg Severity</div>
                  <div className="text-sm font-medium text-foreground">{patient?.avgSeverity}/10</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskLevelColor(patient?.riskLevel)}`}>
                    {patient?.riskLevel?.charAt(0)?.toUpperCase() + patient?.riskLevel?.slice(1)}
                  </span>
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${
                      patient?.isActiveToday ? 'bg-success' : 'bg-muted-foreground'
                    }`} />
                    <span className="text-xs text-muted-foreground">
                      {patient?.isActiveToday ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onPatientSelect(patient);
                    }}
                    iconName="Eye"
                  />
                  <Button
                    variant="default"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onGenerateAISummary(patient);
                    }}
                    iconName="Brain"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Empty State */}
      {filteredAndSortedPatients?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No patients found</h3>
          <p className="text-muted-foreground">
            {searchTerm || filterBy !== 'all' ?'Try adjusting your search or filter criteria.' :'No patients have been registered yet.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default PatientListTable;